import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { 
  SiReact, 
  SiAngular, 
  SiVuedotjs, 
  SiNodedotjs, 
  SiPython, 
  SiJavascript,
  SiTypescript,
  SiPhp,
  SiDjango,
  SiFlask,
  SiExpress,
  SiNestjs,
  SiMongodb,
  SiPostgresql,
  SiMysql,
  SiFirebase,
  SiAmazonwebservices,
  SiGooglecloud,
  SiMicrosoftazure,
  SiDocker,
  SiKubernetes,
  SiGit,
  SiJira,
  SiTailwindcss,
  SiBootstrap,
  SiFlutter,
  SiSolid,
  SiSolidity,
  SiMicrosoftsqlserver,
  SiDotnet
} from "react-icons/si";

// Tech icon component with hover effects
const TechIcon = ({ icon: Icon, name, category }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="flex flex-col items-center justify-center p-3 sm:p-4 hover:bg-gray-800/50 rounded-lg transition-colors"
      whileHover={{ scale: 1.05, y: -3 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Icon className="text-3xl sm:text-4xl md:text-5xl mb-2 text-primary" />
      <span className="text-xs sm:text-sm font-medium text-white text-center">{t(`tech.${name.toLowerCase().replace(/\./g, '_')}`)}</span>
      <span className="text-xs text-gray-400">{t(`categories.${category.toLowerCase()}`)}</span>
    </motion.div>
  );
};

// Technology category with title and icons
const TechCategory = ({ title, technologies }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="mb-8 sm:mb-12"
    >
      <h3 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-6 ${isRTL ? 'border-r-4 pr-4' : 'border-l-4 pl-4'} border-primary`}>
        {title}
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
        {technologies.map((tech, index) => (
          <TechIcon
            key={index}
            icon={tech.icon}
            name={tech.name}
            category={tech.category}
          />
        ))}
      </div>
    </motion.div>
  );
};

const TechnologiesSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  
  const isSoftwareDevelopmentPage = location.pathname === "/software-development";
  
  const handleExpertAdviceClick = (e) => {
    if (isSoftwareDevelopmentPage) {
      e.preventDefault();
      document.getElementById('software-contact').scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Group technologies by category
  const frontendTech = [
    { icon: SiReact, name: "react", category: "Frontend" },
    { icon: SiFlutter, name: "flutter", category: "Frontend" },
    { icon: SiJavascript, name: "javascript", category: "Frontend" },
    { icon: SiTypescript, name: "typescript", category: "Frontend" },
  ];

  const backendTech = [
    { icon: SiNodedotjs, name: "node_js", category: "Backend" },
    { icon: SiPhp, name: "php", category: "Backend" },
    { icon: SiExpress, name: "express", category: "Backend" },
    { icon: SiSolidity, name: "solidity", category: "Backend" },
    { icon: SiDotnet, name: "dotnet", category: "Backend" },
  ];

  const databaseTech = [
    { icon: SiMongodb, name: "mongodb", category: "Database" },
    { icon: SiPostgresql, name: "postgresql", category: "Database" },
    { icon: SiMysql, name: "mysql", category: "Database" },
    { icon: SiMicrosoftsqlserver, name: "sql_server", category: "Database" },
    { icon: SiFirebase, name: "firebase", category: "Database" },
  ];

  const cloudTech = [
    { icon: SiAmazonwebservices, name: "aws", category: "Cloud" },
    { icon: SiGooglecloud, name: "google_cloud", category: "Cloud" },
    { icon: SiMicrosoftazure, name: "azure", category: "Cloud" },
  ];

  const devOpsTech = [
    { icon: SiDocker, name: "docker", category: "DevOps" },
    { icon: SiKubernetes, name: "kubernetes", category: "DevOps" },
  ];

  const otherTech = [
    { icon: SiGit, name: "git", category: "Tools" },
    { icon: SiJira, name: "jira", category: "Tools" },
    { icon: SiTailwindcss, name: "tailwind_css", category: "Tools" },
    { icon: SiBootstrap, name: "bootstrap", category: "Tools" },
  ];

  return (
    <div id="technologies" className="bg-gray-900 py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 text-white">
            {t("tech.title")} <span className="text-primary">{t("tech.stack")}</span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto px-4">
            {t("tech.description")}
          </p>
        </motion.div>

        <div className="bg-black bg-opacity-50 rounded-xl p-4 sm:p-6 md:p-8 border border-gray-800">
          <TechCategory title={t("categories.frontend")} technologies={frontendTech} />
          <TechCategory title={t("categories.backend")} technologies={backendTech} />
          <TechCategory title={t("categories.database")} technologies={databaseTech} />
          <TechCategory title={t("categories.cloud")} technologies={cloudTech} />
          <TechCategory title={t("categories.devops")} technologies={devOpsTech} />
          <TechCategory title={t("categories.tools")} technologies={otherTech} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 text-center bg-gradient-to-r from-gray-900 to-black p-6 sm:p-8 rounded-lg border border-gray-800"
        >
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-white">
            {t("tech.need_custom_solution")}
          </h3>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto mb-6 sm:mb-8">
            {t("tech.team_help_description")}
          </p>
          <a
            href={isSoftwareDevelopmentPage ? "#software-contact" : "#contact"}
            onClick={handleExpertAdviceClick}
            className="inline-block bg-primary text-black px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors text-sm sm:text-base"
          >
            {t("tech.get_expert_advice")}
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default TechnologiesSection;
